import type { Buyer } from 'c-sdk/Types';

import type { BaseEntity } from '../../BaseEntity';
import type { Agency } from './Agency';
import type { Creative } from './Creative';
import type { Market } from './Market';
import type { CampaignDisplay } from './CampaignDisplay';
import type { Client } from './Client';
import type { BudgetSummary } from './BudgetSummary';
import type {
    Displays_AudienceVersion,
    Displays_DisplayIssues,
    Displays_LineItem,
    Displays_Screen,
} from '../Displays';
import type { Owner } from './Owner';
import type { PopDisplays_LogSourceCampaignIdentifier } from '../PopDisplays';
import type { CampaignMediaAutoGroup, CampaignMediaGroupName } from './PopDisplaysMedia';
import type { File } from './File';
import type { User } from './User';
import type { CampaignInstruction } from './CampaignInstruction';
import type { CampaignOwnerContactedDate } from './CampaignOwnerContactedDate';
import type { CampaignPopShot } from './CampaignPopShot';
import type { MissingDisplay } from './MissingDisplay';

export type CampaignUsers = {
    campaign_id: number | string;
    user_id: number | string;
    link_type: string;
    created_at: string;
    user: User;
};
export type OrderlineSiteListErrors = {
    id: number;
    campaign_id: number;
    orderline_id: string;
    external_campaign_id: string;
    media_owner: string;
    environment: string;
    format_name: string;
    line_start_date: string;
    line_end_date: string;
    quantity: string;
    trading_type: string;
    frame_id: string | null;
    level: string;
    message: string;
    row: LineItemRow;
};
/** The slice of an orderline sitelist error the live dashboard (`reporting/live`) sends. */
export type TalonOrderlineError = Pick<
    OrderlineSiteListErrors,
    | 'id'
    | 'orderline_id'
    | 'message'
    | 'media_owner'
    | 'environment'
    | 'format_name'
    | 'line_start_date'
    | 'line_end_date'
    | 'quantity'
    | 'trading_type'
> & {
    row?: Partial<Pick<LineItemRow, 'Booking_Amended_On' | 'Created_Date'>>;
};
/** A line item data status row under investigation: under-delivering on impacts, unresolved. */
export type LineItemUnderInvestigation = {
    id: number;
    display_media_owner_name: string;
    display_line_item_name: string;
    plays_received: number;
    expected_plays: number;
    impacts_received: number;
    expected_impacts: number;
    impacts_percentage: number;
    plays_percentage: number;
};
export type LineItemRow = {
    Campaign_Id: string;
    Campaign_Name: string;
    Brand_Name: string;
    Lead_Planner: string;
    Team: string;
    Team_Email: string;
    Media_Owner: string;
    Environment: string;
    Format_Name: string;
    Order_Line_Id: string;
    SOT: number;
    Line_Start_Date: DateField;
    Line_End_Date: DateField;
    Quantity: number;
    Schedule_Type: string;
    Day_From: string;
    Day_To: string;
    Day_Part: string;
    Run_Days: string;
    Days_In_Week: number;
    Route_Version: number;
    Trading_Type: string;
    Impacts: number;
    Created_Date: DateField;
    Booking_Amended_On: DateField | null;
};
export type DateField = { date: string; timezone_type: number; timezone: string };

export type Campaign = BaseEntity & {
    type: string;
    name: string;
    reference: string;
    text: string;
    /**
     * The hand-typed insertion order ids (Sidebar "IO ID's") for `vistar_us` and the other DSP types.
     * A `vistar_us2` campaign ignores it: its insertion orders come from the schedule page's rules.
     */
    dsp_insertion_order_ids?: string[];
    show_campaign_values_box: boolean;
    view_to_fill?: number;
    view_to_fill_url?: string | null;
    is_talon: boolean;
    gatherer: boolean;
    scheduler: boolean;
    reviewer: boolean;
    pca_display_types: PCADisplayTypesByEnv;
    delivered_value: number | null;
    bought_value: number | null;
    expected_impacts: number | null;
    fargate_cad_memory_override: number | null;
    progress: {
        started: boolean;
        displays_selected: boolean;
        displays_approved: boolean;
        displays_changed: boolean;
        owner_approved: boolean;
        owners_approved: boolean;
        creative_uploaded: boolean;
        creative_assigned: boolean;
        creatives_uploaded: boolean;
        creatives_assigned: boolean;
        creatives_changed: boolean;
        agency_approved: boolean;
        creatives_approved: boolean;
        campaign_approved: boolean;
        archived: boolean;
        billed: boolean;
        billed_changed: boolean;
        creatives_invalid: boolean;
        agency_seen: boolean;
    };
    quotation_budget: number;
    audience_version_id: number;
    audience_version_ids?: number[];
    pca_end_datetime: string;
    pca_end_setting: string;
    pca_available?: { [key: string]: string };
    flags: {
        dirty: boolean;
        under_configuration: boolean;
    };

    old_booking_summaries: { name: string; url: string }[];
    awaiting_booking_data?: boolean;

    booking_summary_validated?: boolean;
    booking_summary_validated_updated_by?: number; // user id
    booking_summary_validated_updated_at?: string;
    bookingSummaryValidatedUser?: User; // include

    // include
    bookingSummaries?: CampaignBookingSummary[];

    /**
     * The value in force, campaign resolved against buyer. This is what decides whether a feature
     * applies to the campaign at all, and what the reporting tabs key off.
     */
    display_verification_enabled: boolean;
    energy_efficiency_enabled: boolean;

    /**
     * Per-campaign flag exposing Classic Postings and the Combined reporting tab (UL-7974). Added
     * by UL-7975; absent until then, so the frontend also shows both to admins.
     */
    classic_postings_enabled?: boolean;

    /**
     * The campaign's own opinion. `null` means inherit from the buyer, which is the default and is
     * NOT the same as an explicit `false`. Edit these; never the effective values above.
     */
    display_verification_enabled_campaign?: boolean | null;
    energy_efficiency_enabled_campaign?: boolean | null;

    /** What the campaign would inherit -- carried so an override can be flagged as one. */
    display_verification_enabled_buyer?: boolean;
    energy_efficiency_enabled_buyer?: boolean;

    playout_percentage?: string;
    /**
     * The parts behind `playout_percentage`, on the same viewer rule (a media owner gets their
     * own). The API writes 0% when `expected_impacts` is 0, so this is how a real 0% is told apart
     * from "nothing was expected". Null until the campaign's next live CAD run.
     */
    playout_stats?: CampaignPlayoutStats | null;
    impacts_received?: number | null;
    pca_last_run_at?: string;

    request_access_agency_id?: number;
    client_id?: number;
    buyer_id?: number;
    buyer?: Buyer;
    agency_id?: number;
    channel_id?: number;
    date_id?: number;
    reminder_display_id?: number;
    reminder_creative_digital_id?: number;
    reminder_creative_analogue_id?: number;
    creative_id?: number;
    stat_id?: number;
    store_id?: number;
    invoice_id?: number;
    owner_id?: number;
    market_id?: number;
    pca_start_datetime?: string;
    /** First and last logged play, per environment. Read via `c-main/Lib` campaignReportDates. */
    min_cad_entry_playout_start_datetime?: CadPlayoutExtentByEnv;
    max_cad_entry_playout_start_datetime?: CadPlayoutExtentByEnv;
    happydemics_id?: number;

    // user who created the campaign
    user_id?: number;

    internal_owner_id?: number;
    internal_assignee_id?: number;

    internal_notes?: string;
    internal_notes_updated_by?: number; // user id
    internal_notes_updated_at?: string;
    internalNotesUser?: User; // include

    internal_rag_status?: CampaignRagStatus;

    high_priority: boolean;

    // yyyy-MM-dd date strings
    internal_planner_aware?: string[];

    sign_off?: boolean;
    sign_off_updated_by?: number; // user id
    sign_off_updated_at?: string;
    signOffUser?: User; // include

    pca_report_requested_by?: number; // user id
    pca_report_requested_at?: string;
    pcaReportUser?: User; // include

    inflight_report_requested_by?: number; // user id
    inflight_report_requested_at?: string;
    inflightReportUser?: User; // include

    data_status: CampaignDataNotice;

    status: CampaignStatus;

    agency?: Agency;
    client?: Client;
    creative?: Creative;
    date_start?: string;
    date_end?: string;
    market?: Market;

    creatives?: Creative[];
    displays?: CampaignDisplay[];

    // include
    campaignInstructions?: CampaignInstruction[];

    // latest budget summary
    budgetSummary?: BudgetSummary;

    // all budget summaries
    budgetSummaries?: BudgetSummary[];

    // include
    lineItemsCount?: number;
    lineItems?: Displays_LineItem[];

    // include=audienceVersions — the prioritised collection backing audience_version_ids
    audienceVersions?: Displays_AudienceVersion[];

    notices?: CampaignNotices;

    displayIssues?: Displays_DisplayIssues[];

    owners?: CampaignOwner[];

    // include
    // popSettings?: PopSettings;

    // include
    errors?: CampaignError[];

    // include
    creativeThumbnails?: File[];

    // include - the user who created the campaign
    user?: User;

    campaignMediaGroupName?: CampaignMediaGroupName[];
    campaignMediaGroup?: CampaignMediaAutoGroup[];
    campaignMediaAutoGroup?: CampaignMediaAutoGroup[];

    // include
    internalAssignee?: User;

    // include
    internalOwner?: User;

    // include
    users?: User[];
    usersCount?: number;

    // include
    ownerContactedDates?: CampaignOwnerContactedDate[];

    // include
    additionalContacts?: User[];

    // include
    popShots?: CampaignPopShot[];
    popShotsCount?: number;

    // include
    missingDisplays?: MissingDisplay[];
    missingDisplaysCount?: number;

    // include
    logSourceCampaignIdentifiers?: PopDisplays_LogSourceCampaignIdentifier[];
    impression_multiplier?: number | string;
    // include
    happydemics: any;
    campaignUsers?: CampaignUsers[];
    // include
    campaignOrderlineSitelistErrors?: OrderlineSiteListErrors[];
};
export type CampaignDisplaySchedule = Displays_Screen & {
    schedulesCount: number;
    schedules: {
        campaign_id: number;
        id: number;
        isDefault?: boolean;
        name: string;
        template: boolean;
    }[];
};
export type PCADisplayType = 'Classic' | 'Display';

export type PCADisplayTypesByEnv = Record<string, ReadonlyArray<PCADisplayType>>;

/**
 * Keyed by environment like `pca_available`. A plain string is the pre-UL-7919 scalar, which only
 * ever held the live extent; the API returns that until its migration has run.
 */
export type CadPlayoutExtentByEnv = Partial<Record<string, string | null>> | string | null;

export type CampaignPlayoutStats = {
    /** sum(expected_impacts_in_schedule): the percentage's denominator. */
    expected_impacts: number;
    /** sum(audience_received): the percentage's numerator. */
    impacts_received: number;
    /** sum(sum_of_play) / sum(expected_plays_in_schedule) * 100; null when no plays expected. */
    plays_percentage: number | null;
};

export type CampaignOwner = {
    campaign_id: number;
    owner_id: number;
    audience_type?: string;
    last_played?: 'log' | 'media';
    playout_percentage?: string;
    id?: number;
    impressions_per_play?: number;
    audience_version_id?: number;
    last_played_date_by: string;

    owner?: Owner;
};

export type CampaignDataNotice = {
    awaiting_data: boolean;
    inspecting_data: boolean;
    awaiting_booking_plan: boolean;
    inflight_report_sent: boolean;
    pca_report_sent: boolean;
};
export type CampaignNotices = {
    overall: CampaignDataNotice;

    // the key is the name of the line item
    line_items: Record<string, CampaignDataNotice>;

    // the key is the name of the media owner
    media_owners: Record<string, CampaignDataNotice>;
};

export enum CampaignStatus {
    CampaignStarted = 'campaign_started',
    CreativesUploaded = 'creatives_uploaded',
    CampaignReady = 'campaign_ready',
    CampaignArchived = 'campaign_archived',
    CampaignComplete = 'campaign_complete',
    Unknown = 'unknown',
}

export enum CampaignNoticeKey {
    AwaitingData = 'awaiting_data',
    InspectingData = 'inspecting_data',
    AwaitingBookingPlan = 'awaiting_booking_plan',
}

export type CampaignError = BaseEntity & {
    campaign_id: number;

    // id of entity connected to error
    errorable_id: string;

    // the full class path of the entity connected to the error
    errorable_type: CampaignErrorableType;

    type: CampaignErrorType;

    data: {
        expected: string[];
        outcome: string[];
    };
};

export enum CampaignErrorableType {
    Campaign = 'Campaign',
    LineItem = 'LineItem',
    Creative = 'Creative',
}

export enum CampaignErrorType {
    MissingLineItemMimeType = 'missing_line_item_mime_type',
    MissingAnalogueLineItem = 'missing_analogue_line_item',
    NoMatchingLineItemResolutions = 'no_matching_line_item_resolutions',
    CreativeMissingResolution = 'creative_missing_resolution',
    IncorrectAnimation = 'incorrect_animation',
    IncorrectImageAnimation = 'incorrect_image_animation',
    IncorrectVideoAnimation = 'incorrect_video_animation',
    IncorrectFileAnalogue = 'incorrect_file_analogue',

    // Campaign level errors
    NoMatchingResolution = 'no_matching_resolution',
    MissingCreativeResolutions = 'missing_creative_resolutions',
    NoMatchingResolutionCreatives = 'no_matching_resolution_creatives',
    NoMatchingImageCreatives = 'no_matching_image_creatives',
    NoMatchingAnalogueCreatives = 'no_matching_analogue_creatives',
    NoMatchingVideoCreatives = 'no_matching_video_creatives',
}

export enum CampaignRagStatus {
    Red = 'red',
    Amber = 'amber',
    Green = 'green',
}

export type CampaignBookingSummary = BaseEntity & {
    raw_path: string;
    path: string;
    name: string;

    campaign_id: number;
    user_id?: number;

    // include
    user?: User;
};
